/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  CampaignMessageStatus,
  CampaignTemplateChannel,
  ICampaignMessage,
  ICampaignRecipient,
  ICampaignRequest,
  ICampaignResponse,
  ICampaignTemplate,
  ICampaignTriggerKeyword,
  ICampaignTriggerMessage,
} from '@dataTypes/marketing.type'
import {
  createCampaignTemplate,
  deleteCampaignTemplateById,
  getAllCampaignTemplates,
  updateCampaignTemplateById,
  createCampaign,
  deleteCampaignById,
  getAllCampaigns,
  sendBulkSms,
  updateCampaignById,
  deleteMultipleCampaignRecipients,
  createMultipleCampaignRecipients,
  deleteMultipleTriggerKeywords,
  createMultipleTriggerKeywords,
  updateMultipleTriggerKeywords,
  updateCampaignMessageBySID,
  createMultipleCampaignMessages,
  sendScheduledBulkSms,
  sendBulkEmail,
  sendScheduledBulkEmail,
  sendSms,
  deleteCampaignRecipientByContactId,
} from '@services/api'
import { logger } from '@utils/safeLogger'
import {
  createMultipleTriggerMessages,
  deleteMultipleTriggerMessages,
  updateMultipleTriggerMessages,
} from '@services/api/marketing/campaign-trigger-message.api'
import { RootState } from '@store/index'
import { getMockMarketingCampaigns } from '@assets/mockdata/Marketing/marketingCampaigns.mock'
import { getMockCampaignTemplates } from '@assets/mockdata/Marketing/marketingCampaignTemplates.mock'
import { toPersistedCampaignTemplateId } from '@utils/campaign.util'
import { getEnv } from '@utils/env'
import { parseEmailEditorCanvasFromHtml } from '@utils/marketingTemplate.util'

function marketingApiForcedLive(): boolean {
  const v = getEnv('REACT_APP_FORCE_LIVE_MARKETING_API').trim().toLowerCase()
  return v === 'true' || v === '1' || v === 'yes'
}

function envFlagTrue(v: string | undefined): boolean {
  const t = v?.trim().toLowerCase()
  return t === 'true' || t === '1' || t === 'yes'
}

function localStorageFlagTrue(key: string): boolean {
  if (typeof window === 'undefined') return false
  try {
    const t = window.localStorage.getItem(key)?.trim().toLowerCase()
    return t === '1' || t === 'true' || t === 'yes'
  } catch {
    return false
  }
}

/** Unified dev toggle (build-time or runtime `window._env_`). Enables campaign + template mocks. */
function useMarketingUnifiedMockData(): boolean {
  return envFlagTrue(getEnv('REACT_APP_USE_MARKETING_MOCK_DATA'))
}

// Campaigns
export const fetchAllCampaigns = createAsyncThunk<
  ICampaignResponse[],
  void,
  {
    rejectValue: string
  }
>('api/marketing/campaign/fetch/all', async (_, { rejectWithValue }) => {
  if (getEnv('NODE_ENV') === 'development' && !marketingApiForcedLive()) {
    if (
      useMarketingUnifiedMockData() ||
      envFlagTrue(getEnv('REACT_APP_MOCK_MARKETING_CAMPAIGNS')) ||
      localStorageFlagTrue('mockMarketingCampaigns')
    ) {
      return getMockMarketingCampaigns()
    }
  }

  try {
    const response = await getAllCampaigns()

    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }

    if (
      response.data &&
      'campaigns' in response.data &&
      Array.isArray(response.data.campaigns)
    ) {
      const _campaigns = response.data.campaigns as ICampaignResponse[]
      return _campaigns
    }

    return rejectWithValue('Campaigns not found in the response')
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

export const addCampaign = createAsyncThunk<
  ICampaignRequest,
  ICampaignRequest,
  { rejectValue: string }
>('api/marketing/campaign/add', async (req, { getState, rejectWithValue }) => {
  const state = getState() as RootState
  const clinicId = state.clinic.selectedClinic?.clinic_id as string
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    const response = await createCampaign({
      ...req,
      campaign_template_id: toPersistedCampaignTemplateId(
        req.campaign_template_id,
      ),
      clinic_id: clinicId,
      created_by: user.user_id,
      updated_by: user.user_id,
    })

    if (response.statusCode !== 201) {
      return rejectWithValue(
        response.message || 'Failed to create new campaign',
      )
    }

    if (
      response.data &&
      'campaign' in response.data &&
      typeof response.data.campaign === 'object'
    ) {
      const _campaign = response.data.campaign as ICampaignRequest
      return _campaign
    }
    return response.data.campaign as ICampaignRequest
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to create campaign')
  }
})

export const updateCampaign = createAsyncThunk<
  ICampaignRequest,
  { campaign_id: string; data: Partial<ICampaignResponse> },
  { rejectValue: string }
>(
  'api/marketing/campaign/update',
  async ({ campaign_id, data }, { rejectWithValue }) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const response = await updateCampaignById(campaign_id, {
        ...data,
        updated_by: user.user_id,
      })

      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }

      if (
        response.data &&
        'campaign' in response.data &&
        typeof response.data.campaign === 'object'
      ) {
        const _campaign = response.data.campaign as ICampaignResponse
        return _campaign
      }

      return rejectWithValue('Campaign not found in the response')
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error occurred during update')
    }
  },
)

export const deleteCampaign = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('api/marketing/campaign/delete', async (id, { rejectWithValue }) => {
  try {
    const response = await deleteCampaignById(id)

    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }
    return id
  } catch (error) {
    return rejectWithValue('Failed to delete campaign')
  }
})
// End of Campaign actions

// Campaign templates
export const fetchAllCampaignTemplates = createAsyncThunk<
  ICampaignTemplate[],
  void,
  {
    rejectValue: string
  }
>('api/marketing/template/fetch/all', async (_, { rejectWithValue }) => {
  if (getEnv('NODE_ENV') === 'development' && !marketingApiForcedLive()) {
    const mockCampaigns =
      useMarketingUnifiedMockData() ||
      envFlagTrue(getEnv('REACT_APP_MOCK_MARKETING_CAMPAIGNS')) ||
      localStorageFlagTrue('mockMarketingCampaigns')
    if (
      envFlagTrue(getEnv('REACT_APP_MOCK_MARKETING_TEMPLATES')) ||
      localStorageFlagTrue('mockMarketingTemplates') ||
      mockCampaigns
    ) {
      return getMockCampaignTemplates()
    }
  }

  try {
    const response = await getAllCampaignTemplates()

    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }

    if (
      response.data &&
      'campaign_templates' in response.data &&
      Array.isArray(response.data.campaign_templates)
    ) {
      const apiTemplates = response.data
        .campaign_templates as ICampaignTemplate[]
      return apiTemplates.map((tpl) => {
        if (
          tpl.email_editor_canvas ||
          tpl.channel !== CampaignTemplateChannel.email
        ) {
          return tpl
        }
        // Fallback for older HTML that still embeds canvas as a data attribute
        const canvas = parseEmailEditorCanvasFromHtml(tpl.html_content)
        return canvas ? { ...tpl, email_editor_canvas: canvas } : tpl
      })
    }

    return rejectWithValue('Campaign templates not found in the response')
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

export const addCampaignTemplate = createAsyncThunk<
  void,
  ICampaignTemplate,
  { rejectValue: string }
>(
  'api/marketing/template/add',
  async (templateData, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as RootState
    const clinicId = state.clinic.selectedClinic?.clinic_id as string
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const response = await createCampaignTemplate({
        ...templateData,
        channel:
          templateData.channel ||
          (state.marketing.templatesListChannel === 'Email'
            ? CampaignTemplateChannel.email
            : CampaignTemplateChannel.sms),
        clinic_id: clinicId,
        created_by: user.user_id,
      })

      if (response.statusCode !== 201) {
        return rejectWithValue(
          response.message || 'Failed to create new campaign template',
        )
      }

      dispatch(fetchAllCampaignTemplates())
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to create campaign template',
      )
    }
  },
)

export const deleteCampaignTemplate = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('api/marketing/template/delete', async (id, { rejectWithValue }) => {
  try {
    const response = await deleteCampaignTemplateById(id)

    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }
    return id
  } catch (error) {
    return rejectWithValue('Failed to delete campaign template')
  }
})

export const updateCampaignTemplate = createAsyncThunk<
  ICampaignTemplate,
  { campaign_template_id: string; data: Partial<ICampaignTemplate> },
  { rejectValue: string }
>(
  'api/marketing/template/update',
  async ({ campaign_template_id, data }, { rejectWithValue }) => {
    try {
      const response = await updateCampaignTemplateById(
        campaign_template_id,
        data,
      )

      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }

      if (
        response.data &&
        'campaign_template' in response.data &&
        typeof response.data.campaign_template === 'object'
      ) {
        return response.data.campaign_template as ICampaignTemplate
      }

      return rejectWithValue('Campaign template not found in the response')
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error occurred during update')
    }
  },
)
// End of Campaign template actions

// Campaign tags, channels,
// TODO: Uncomment this when Email feature is implemented
// export const addCampaignTag = createAsyncThunk<
//   void,
//   ICampaignTag,
//   { rejectValue: string }
// >('api/marketing/tag/add', async (tagData, { dispatch, rejectWithValue }) => {
//   try {
//     const response = await createCampaignTag(tagData)

//     if (response.statusCode !== 201) {
//       return rejectWithValue(
//         response.message || 'Failed to create new campaign tag',
//       )
//     }

//     dispatch(fetchAllCampaignTemplates())
//   } catch (error: any) {
//     return rejectWithValue(error.message || 'Failed to create campaign tag')
//   }
// })

// export const addCampaignChannel = createAsyncThunk<
//   void,
//   ICampaignChannel,
//   { rejectValue: string }
// >(
//   'api/marketing/channel/add',
//   async (channelData, { dispatch, rejectWithValue }) => {
//     try {
//       const channelResponse = await createCampaignChannel(channelData)
//       if (channelResponse.statusCode !== 201) {
//         return rejectWithValue(
//           channelResponse.message || 'Failed to create new campaign channel',
//         )
//       }

//       dispatch(fetchAllCampaigns())
//     } catch (error: any) {
//       return rejectWithValue(error.message || 'Failed to create campaign')
//     }
//   },
// )

// TODO: Uncomment this when Email feature is implemented
// export const deleteCampaignTags = createAsyncThunk<
//   string[],
//   string[],
//   { rejectValue: string }
// >('api/marketing/tag/delete', async (ids, { rejectWithValue }) => {
//   try {
//     const response = await deleteMultipleCampaignTagsById(ids)

//     if (response.statusCode !== 200) {
//       return rejectWithValue(response.message)
//     }
//     return ids
//   } catch (error) {
//     return rejectWithValue('Failed to delete campaign tag')
//   }
// })

// Campaign recipients
export const bulkAddCampaignRecipient = createAsyncThunk<
  ICampaignRecipient[],
  ICampaignRecipient[],
  { rejectValue: string }
>(
  'api/marketing/campaign/recipient/add',
  async (recipients, { rejectWithValue }) => {
    try {
      const response = await createMultipleCampaignRecipients(recipients)

      if (response.statusCode !== 201) {
        return rejectWithValue(response.message)
      }

      if (
        response.data &&
        'campaign_recipients' in response.data &&
        Array.isArray(response.data.campaign_recipients)
      ) {
        return response.data.campaign_recipients as ICampaignRecipient[]
      }

      return recipients
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to add campaign recipients',
      )
    }
  },
)

export const bulkDeleteCampaignRecipient = createAsyncThunk<
  string[],
  string[],
  { rejectValue: string }
>(
  'api/marketing/campaign/recipient/delete',
  async (ids, { rejectWithValue }) => {
    try {
      const response = await deleteMultipleCampaignRecipients(ids)

      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }
      return ids
    } catch (error) {
      return rejectWithValue('Failed to delete campaign recipients')
    }
  },
)

export const optOutCampaignRecipient = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>(
  'api/marketing/campaign/recipient/opt-out',
  async (contactId, { rejectWithValue }) => {
    try {
      const response = await deleteCampaignRecipientByContactId(contactId)

      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }
      return contactId
    } catch (error) {
      return rejectWithValue('Failed to opt out campaign recipient')
    }
  },
)

// End of Campaign recipient actions

// Campaign trigger keywords
export const bulkAddKeywords = createAsyncThunk<
  ICampaignTriggerKeyword[],
  ICampaignTriggerKeyword[],
  { rejectValue: string }
>(
  'api/marketing/campaign/trigger-keyword/add',
  async (keywords, { rejectWithValue }) => {
    try {
      const response = await createMultipleTriggerKeywords(keywords)

      if (response.statusCode !== 201) {
        return rejectWithValue(response.message)
      }

      if (
        response.data &&
        'campaign_trigger_keywords' in response.data &&
        Array.isArray(response.data.campaign_trigger_keywords)
      ) {
        return response.data
          .campaign_trigger_keywords as ICampaignTriggerKeyword[]
      }

      return keywords
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to add campaign trigger keywords',
      )
    }
  },
)

export const bulkUpdateKeywords = createAsyncThunk<
  ICampaignTriggerKeyword[],
  ICampaignTriggerKeyword[],
  { rejectValue: string }
>(
  'api/marketing/campaign/trigger-keyword/update',
  async (keywords, { rejectWithValue }) => {
    try {
      const response = await updateMultipleTriggerKeywords(keywords)

      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }

      return keywords
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to update campaign trigger keywords',
      )
    }
  },
)

export const bulkDeleteKeywords = createAsyncThunk<
  string[],
  string[],
  { rejectValue: string }
>(
  'api/marketing/campaign/trigger-keyword/delete',
  async (ids, { rejectWithValue }) => {
    try {
      const response = await deleteMultipleTriggerKeywords(ids)

      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }
      return ids
    } catch (error) {
      return rejectWithValue('Failed to delete campaign trigger keyword')
    }
  },
)
// End of Campaign trigger keyword actions

// Campaign trigger messages
export const bulkAddTriggerMessages = createAsyncThunk<
  ICampaignTriggerMessage[],
  ICampaignTriggerMessage[],
  { rejectValue: string }
>(
  'api/marketing/campaign/trigger-message/add',
  async (messages, { rejectWithValue }) => {
    try {
      const response = await createMultipleTriggerMessages(messages)

      if (response.statusCode !== 201) {
        return rejectWithValue(response.message)
      }

      if (
        response.data &&
        'campaign_trigger_messages' in response.data &&
        Array.isArray(response.data.campaign_trigger_messages)
      ) {
        return response.data
          .campaign_trigger_messages as ICampaignTriggerMessage[]
      }

      return messages
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to add campaign trigger messages',
      )
    }
  },
)

export const bulkUpdateTriggerMessages = createAsyncThunk<
  ICampaignTriggerMessage[],
  ICampaignTriggerMessage[],
  { rejectValue: string }
>(
  'api/marketing/campaign/trigger-message/update',
  async (messages, { rejectWithValue }) => {
    try {
      const response = await updateMultipleTriggerMessages(messages)

      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }

      return messages
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to update campaign trigger messages',
      )
    }
  },
)

export const bulkDeleteTriggerMessages = createAsyncThunk<
  string[],
  string[],
  { rejectValue: string }
>(
  'api/marketing/campaign/trigger-message/delete',
  async (ids, { rejectWithValue }) => {
    try {
      const response = await deleteMultipleTriggerMessages(ids)

      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }
      return ids
    } catch (error) {
      return rejectWithValue('Failed to delete campaign trigger keyword')
    }
  },
)
// End of Campaign trigger message actions

// Campaign messages
export const bulkAddCampaignMessages = createAsyncThunk<
  ICampaignMessage[],
  ICampaignMessage[],
  { rejectValue: string }
>(
  'api/marketing/campaign/message/add',
  async (messages, { rejectWithValue }) => {
    try {
      const response = await createMultipleCampaignMessages(messages)

      if (response.statusCode !== 201) {
        return rejectWithValue(response.message)
      }

      if (
        response.data &&
        'campaign_messages' in response.data &&
        Array.isArray(response.data.campaign_messages)
      ) {
        return response.data.campaign_messages as ICampaignMessage[]
      }

      return messages
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to add campaign messages')
    }
  },
)

export const updateCampaignMessage = createAsyncThunk<
  {
    campaign_message_id?: string
    message_sid?: string
    data: Partial<ICampaignMessage>
  },
  {
    campaign_message_id?: string
    message_sid?: string
    data: Partial<ICampaignMessage>
  },
  { rejectValue: string }
>(
  'api/marketing/campaign/message/update',
  async ({ campaign_message_id, message_sid, data }, { rejectWithValue }) => {
    try {
      const response = await updateCampaignMessageBySID(
        data,
        campaign_message_id,
        message_sid,
      )

      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }

      return { campaign_message_id, message_sid, data }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error occurred during update')
    }
  },
)
// End of Campaign message actions

const CAMPAIGN_MESSAGE_CHUNK_SIZE = 50

type CampaignMessagesApiResponse = {
  statusCode: number
  message: string
  data?: { campaign_messages?: ICampaignMessage[] }
}

type BulkSmsProviderFn = (
  sid: string,
  token: string,
  messages: ICampaignMessage[],
) => Promise<CampaignMessagesApiResponse>

type BulkEmailProviderFn = (
  messages: ICampaignMessage[],
  clinicId?: string,
) => Promise<CampaignMessagesApiResponse>

const createCampaignMessagesInChunks = async (
  messages: ICampaignMessage[],
): Promise<ICampaignMessage[]> => {
  const pendingMessages = messages.map((message) => ({
    ...message,
    status: CampaignMessageStatus.pending,
  }))

  const saved: ICampaignMessage[] = []
  for (
    let i = 0;
    i < pendingMessages.length;
    i += CAMPAIGN_MESSAGE_CHUNK_SIZE
  ) {
    const chunk = pendingMessages.slice(i, i + CAMPAIGN_MESSAGE_CHUNK_SIZE)
    const response = await createMultipleCampaignMessages(chunk)

    if (response.statusCode !== 201) {
      throw new Error(response.message || 'Failed to save campaign messages')
    }

    if (
      response.data &&
      'campaign_messages' in response.data &&
      Array.isArray(response.data.campaign_messages)
    ) {
      saved.push(...(response.data.campaign_messages as ICampaignMessage[]))
    } else {
      saved.push(...chunk)
    }
  }

  return saved
}

const campaignSendPersistFields = (
  message: ICampaignMessage,
): Partial<ICampaignMessage> => {
  const updatePayload: Partial<ICampaignMessage> = {
    message_sid: message.message_sid,
    error_code: message.error_code,
    error_reason: message.error_reason,
    resend_count: message.resend_count,
  }

  if (message.status === CampaignMessageStatus.failed) {
    updatePayload.status = CampaignMessageStatus.failed
    updatePayload.date_time_sent = null
  } else if (message.status === CampaignMessageStatus.sent) {
    updatePayload.status = CampaignMessageStatus.sent
    if (message.date_time_sent) {
      updatePayload.date_time_sent = message.date_time_sent
    }
  } else {
    updatePayload.status = CampaignMessageStatus.pending
  }

  return updatePayload
}

const updateCampaignMessagesAfterSend = async (
  sentMessages: ICampaignMessage[],
): Promise<ICampaignMessage[]> => {
  return Promise.all(
    sentMessages.map(async (message) => {
      if (!message.campaign_message_id) {
        throw new Error('Campaign message ID missing after save')
      }

      const updatePayload = campaignSendPersistFields(message)

      const response = await updateCampaignMessageBySID(
        updatePayload,
        message.campaign_message_id,
        message.message_sid,
      )

      if (response.statusCode !== 200) {
        throw new Error(
          response.message || 'Failed to update campaign message after send',
        )
      }

      return { ...message, ...updatePayload }
    }),
  )
}

const persistThenSendCampaignMessages = async (
  messages: ICampaignMessage[],
  sid: string | undefined,
  token: string | undefined,
  sendFn: BulkSmsProviderFn,
): Promise<ICampaignMessage[]> => {
  const savedMessages = await createCampaignMessagesInChunks(messages)

  if (!sid || !token) {
    return savedMessages
  }

  const response = await sendFn(sid, token, savedMessages)

  if (response.statusCode !== 201) {
    throw new Error(response.message || 'Failed to send campaign SMS')
  }

  if (
    !response.data ||
    !('campaign_messages' in response.data) ||
    !Array.isArray(response.data.campaign_messages)
  ) {
    throw new Error('Invalid response from SMS provider')
  }

  return updateCampaignMessagesAfterSend(
    response.data.campaign_messages as ICampaignMessage[],
  )
}

/** Same order as SMS: persist Pending rows → provider send → update status/sent time. */
const persistThenSendCampaignEmails = async (
  messages: ICampaignMessage[],
  clinicId: string | undefined,
  sendFn: BulkEmailProviderFn,
): Promise<ICampaignMessage[]> => {
  const savedMessages = await createCampaignMessagesInChunks(messages)

  if (!clinicId) {
    return savedMessages
  }

  const response = await sendFn(savedMessages, clinicId)

  if (response.statusCode !== 201) {
    throw new Error(response.message || 'Failed to send campaign email')
  }

  if (
    !response.data ||
    !('campaign_messages' in response.data) ||
    !Array.isArray(response.data.campaign_messages)
  ) {
    throw new Error('Invalid response from email provider')
  }

  return updateCampaignMessagesAfterSend(
    response.data.campaign_messages as ICampaignMessage[],
  )
}

// External API actions
export const sendCampaignSms = createAsyncThunk<
  ICampaignMessage[],
  ICampaignMessage[],
  { rejectValue: string }
>(
  'api/marketing/campaign/send-sms',
  async (messages, { getState, rejectWithValue }) => {
    try {
      const selectedClinic = (getState() as RootState).clinic.selectedClinic
      const sid = selectedClinic?.twilio_account_sid
      const token = selectedClinic?.twilio_auth_token

      return await persistThenSendCampaignMessages(
        messages,
        sid,
        token,
        sendBulkSms,
      )
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to send campaign sms'
      return rejectWithValue(message)
    }
  },
)

export const scheduleSendSms = createAsyncThunk<
  ICampaignMessage[],
  ICampaignMessage[],
  { rejectValue: string }
>(
  'api/marketing/campaign/schedule-send-sms',
  async (messages, { getState, rejectWithValue }) => {
    try {
      const selectedClinic = (getState() as RootState).clinic.selectedClinic
      const sid = selectedClinic?.twilio_account_sid
      const token = selectedClinic?.twilio_auth_token

      return await persistThenSendCampaignMessages(
        messages,
        sid,
        token,
        sendScheduledBulkSms,
      )
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to schedule send campaign sms'
      return rejectWithValue(message)
    }
  },
)

export const sendCampaignEmail = createAsyncThunk<
  ICampaignMessage[],
  ICampaignMessage[],
  { rejectValue: string }
>(
  'api/marketing/campaign/send-email',
  async (messages, { getState, rejectWithValue }) => {
    try {
      const selectedClinic = (getState() as RootState).clinic.selectedClinic
      const clinicId = selectedClinic?.clinic_id

      return await persistThenSendCampaignEmails(
        messages,
        clinicId,
        sendBulkEmail,
      )
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to send campaign email'
      return rejectWithValue(message)
    }
  },
)

export const scheduleSendEmail = createAsyncThunk<
  ICampaignMessage[],
  ICampaignMessage[],
  { rejectValue: string }
>(
  'api/marketing/campaign/schedule-send-email',
  async (messages, { getState, rejectWithValue }) => {
    try {
      const selectedClinic = (getState() as RootState).clinic.selectedClinic
      const clinicId = selectedClinic?.clinic_id

      return await persistThenSendCampaignEmails(
        messages,
        clinicId,
        sendScheduledBulkEmail,
      )
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to schedule send campaign email'
      return rejectWithValue(message)
    }
  },
)

export const resendBouncedSms = createAsyncThunk<
  Partial<ICampaignMessage>,
  ICampaignMessage,
  { rejectValue: string }
>(
  'api/marketing/campaign/resend-sms',
  async (message, { getState, rejectWithValue }) => {
    try {
      const selectedClinic = (getState() as RootState).clinic.selectedClinic
      const sid = selectedClinic?.twilio_account_sid
      const token = selectedClinic?.twilio_auth_token

      let dataToBeSaved = { ...message }

      if (sid && token) {
        const response = await sendSms(sid, token, message)

        if (response.statusCode !== 201) {
          logger.error('Error sending campaign SMS', {
            message: response.message,
          })
          return rejectWithValue(response.message)
        }

        if (
          response.data &&
          'campaign_message' in response.data &&
          typeof response.data.campaign_message === 'object'
        ) {
          dataToBeSaved = {
            ...message,
            ...(response.data.campaign_message as Partial<ICampaignMessage>),
          }

          const updatedData = await updateCampaignMessageBySID(
            campaignSendPersistFields(dataToBeSaved),
            message.campaign_message_id,
            dataToBeSaved.message_sid,
          )

          if (updatedData.statusCode !== 200) {
            logger.error('Error sending campaign SMS', {
              message: response.message,
            })
          }

          return dataToBeSaved
        }
      } else {
        const errorData = {
          error_code: '404',
          error_reason: 'Clinic is not configured to send SMS',
          status: CampaignMessageStatus.failed,
        }
        await updateCampaignMessageBySID(
          errorData,
          message.campaign_message_id,
          message.message_sid,
        )

        dataToBeSaved = {
          ...message,
          ...errorData,
        }
      }

      return dataToBeSaved
    } catch (error) {
      return rejectWithValue('Failed to resend bounced campaign sms')
    }
  },
)

export const sendTriggerKeywordMessage = createAsyncThunk<
  ICampaignMessage,
  ICampaignMessage,
  { rejectValue: string }
>(
  'api/marketing/campaign/send-trigger-keyword-message',
  async (message, { getState, rejectWithValue }) => {
    try {
      const selectedClinic = (getState() as RootState).clinic.selectedClinic
      const sid = selectedClinic?.twilio_account_sid
      const token = selectedClinic?.twilio_auth_token

      let dataToBeSaved = { ...message }

      if (sid && token) {
        const response = await sendSms(sid, token, message)

        if (response.statusCode !== 201) {
          logger.error('Error sending campaign SMS', {
            message: response.message,
          })
          return rejectWithValue(response.message)
        }

        if (
          response.data &&
          'campaign_message' in response.data &&
          typeof response.data.campaign_message === 'object'
        ) {
          dataToBeSaved = {
            ...message,
            ...(response.data.campaign_message as Partial<ICampaignMessage>),
          }
        }
      }

      await createMultipleCampaignMessages([dataToBeSaved])
      return dataToBeSaved
    } catch (error) {
      return rejectWithValue('Failed to send trigger keyword message')
    }
  },
)

// Local actions
export const setSelectedDate = createAsyncThunk(
  'api/marketing/overview/date',
  async (newDate: Date): Promise<string> => {
    return newDate.toISOString()
  },
)

export const setShowTemplatePreview = createAsyncThunk(
  'api/marketing/template/preview',
  async (show: boolean): Promise<boolean> => {
    return show
  },
)

export const setTemplate = createAsyncThunk(
  'api/marketing/template/set',
  async ({
    template,
    type,
  }: {
    template: ICampaignTemplate | null
    type: 'selected' | 'current'
  }): Promise<{
    template: ICampaignTemplate | null
    type: 'selected' | 'current'
  }> => {
    return { template, type }
  },
)

export const setTemplateError = createAsyncThunk(
  'api/marketing/template/error',
  async (payload: { error: string | null; type: 'template' | 'hyperlink' }) => {
    return payload
  },
)
